import React, { useEffect, useState } from 'react';
import { Button, Input, Option, Select, Spinner } from '@material-tailwind/react';
import { ModalX } from '../../components';
import { extractCoordinates } from '../../utils/functions';
import { useAddBranch, useUpdateBranch } from '../../custom-hooks/use-branches';

const PAKISTAN_CITIES = [
  { value: 'lahore', label: 'Lahore' },
  { value: 'karachi', label: 'Karachi' },
  { value: 'islamabad', label: 'Islamabad' },
  { value: 'rawalpindi', label: 'Rawalpindi' },
  { value: 'faisalabad', label: 'Faisalabad' },
  { value: 'multan', label: 'Multan' },
  { value: 'peshawar', label: 'Peshawar' },
  { value: 'quetta', label: 'Quetta' },
  { value: 'hyderabad', label: 'Hyderabad' },
  { value: 'gujranwala', label: 'Gujranwala' },
  { value: 'gujrat', label: 'Gujrat' },
  { value: 'sialkot', label: 'Sialkot' },
  { value: 'sargodha', label: 'Sargodha' },
  { value: 'bahawalpur', label: 'Bahawalpur' },
  { value: 'abbottabad', label: 'Abbottabad' },
  { value: 'sheikhupura', label: 'Sheikhupura' },
  { value: 'sukkur', label: 'Sukkur' },
  { value: 'larkana', label: 'Larkana' },
  { value: 'murree', label: 'Murree' },
  { value: 'muzaffarabad', label: 'Muzaffarabad' },
  { value: 'mirpur', label: 'Mirpur' },
  { value: 'gwadar', label: 'Gwadar' },
  { value: 'mardan', label: 'Mardan' },
  { value: 'sahiwal', label: 'Sahiwal' },
  { value: 'okara', label: 'Okara' },
  { value: 'jhelum', label: 'Jhelum' },
  { value: 'kasur', label: 'Kasur' },
  { value: 'rahim_yar_khan', label: 'Rahim Yar Khan' },
  { value: 'dera_ghazi_khan', label: 'Dera Ghazi Khan' },
];

const INITIAL_FORM_DATA = {
  branchName: '',
  branchPhone: '',
  address: '',
  city: 'lahore',
  state: 'punjab',
  postalCode: '',
  country: 'pakistan',
  googleMapsLink: '',
  lng: '',
  lat: '',
  email: '',
  website: '',
};

const BranchCrudModal = ({ isOpen, onClose, branch = null }) => {
  const isUpdating = !!branch?._id;
  const addBranchMutation = useAddBranch();
  const updateBranchMutation = useUpdateBranch();
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [googleMapsLinkStatus, setGoogleMapsLinkStatus] = useState('');
  const [isLoadingCoordinates, setIsLoadingCoordinates] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (branch) {
      setFormData({
        ...INITIAL_FORM_DATA,
        branchName: branch.branchName || '',
        branchPhone: branch.branchPhone || '',
        email: branch.email || '',
        website: branch.website || '',
        address: branch.address || '',
        city: branch.city || INITIAL_FORM_DATA.city,
        state: branch.state || INITIAL_FORM_DATA.state,
        country: branch.country || INITIAL_FORM_DATA.country,
        postalCode: branch.postalCode || '',
        googleMapsLink: branch.googleMapsLink || '',
        lng: branch.lng ?? '',
        lat: branch.lat ?? '',
      });
    } else {
      setFormData(INITIAL_FORM_DATA);
    }
    setGoogleMapsLinkStatus('');
  }, [isOpen, branch]);

  const isSubmitting = addBranchMutation.isLoading || updateBranchMutation.isLoading;

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === 'googleMapsLink' && value !== '') {
      setIsLoadingCoordinates(true);
      const coords = await extractCoordinates(value);
      setIsLoadingCoordinates(false);
      if (coords.status === 200) {
        setGoogleMapsLinkStatus('Valid Link');
        setFormData((prev) => ({
          ...prev,
          googleMapsLink: value,
          lng: coords.longitude,
          lat: coords.latitude,
        }));
      } else {
        setGoogleMapsLinkStatus('Invalid Location Link');
        setFormData((prev) => ({ ...prev, lng: '', lat: '' }));
      }
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      name: formData.branchName,
      phone: formData.branchPhone,
      email: formData.email,
      website: formData.website,
      address: formData.address,
      city: formData.city,
      state: formData.state,
      postalCode: formData.postalCode,
      country: formData.country,
      location: {
        googleMapsLink: formData.googleMapsLink,
        lng: parseFloat(formData.lng),
        lat: parseFloat(formData.lat),
      },
    };

    if (isUpdating) {
      await updateBranchMutation.mutateAsync({ id: branch._id, data: payload });
    } else {
      await addBranchMutation.mutateAsync(payload);
    }
    onClose();
  };

  return (
    <ModalX
      isOpen={isOpen}
      onClose={() => !isSubmitting && onClose()}
      title={isUpdating ? 'Update Branch' : 'Add Branch'}
      maxWidth="xxl"
    >
      <form onSubmit={handleFormSubmit}>
        <div className="space-y-4">
          <div className="flex gap-4 flex-col md:flex-row">
            <Input
              label="Branch Name"
              name="branchName"
              value={formData.branchName}
              onChange={handleInputChange}
              required
              disabled={isSubmitting}
            />
            <Input
              label="Branch Phone"
              name="branchPhone"
              value={formData.branchPhone}
              onChange={handleInputChange}
              required
              disabled={isSubmitting}
            />
          </div>
          <div className="flex gap-4 flex-col md:flex-row">
            <Input
              label="Branch Email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              disabled={isSubmitting}
            />
            <Input
              label="Website"
              name="website"
              value={formData.website}
              onChange={handleInputChange}
              disabled={isSubmitting}
            />
          </div>
          <div className="flex gap-4 flex-col md:flex-row">
            <Select
              label="Country"
              name="country"
              value={formData.country}
              onChange={(value) => handleSelectChange('country', value)}
              disabled={isSubmitting}
            >
              <Option value="pakistan">Pakistan</Option>
            </Select>
            <Select
              label="State"
              name="state"
              value={formData.state}
              onChange={(value) => handleSelectChange('state', value)}
              disabled={isSubmitting}
            >
              <Option value="sindh">Sindh</Option>
              <Option value="kpk">Khyber Pakhtunkhwa</Option>
              <Option value="balochistan">Balochistan</Option>
              <Option value="punjab">Punjab</Option>
              <Option value="gilgit_baltistan">Gilgit-Baltistan</Option>
              <Option value="ajk">Azad Jammu & Kashmir</Option>
              <Option value="ict">Islamabad Capital Territory</Option>
            </Select>
            <Select
              label="City"
              name="city"
              value={formData.city}
              onChange={(value) => handleSelectChange('city', value)}
              disabled={isSubmitting}
            >
              {PAKISTAN_CITIES.map(({ value, label }) => (
                <Option key={value} value={value}>{label}</Option>
              ))}
            </Select>
          </div>
          <div className="flex w-full gap-4 flex-col md:flex-row">
            <Input
              label="Address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              required
              disabled={isSubmitting}
              containerProps={{ className: 'min-w-0' }}
            />
            <Input
              label="Postal Code"
              name="postalCode"
              value={formData.postalCode}
              onChange={handleInputChange}
              disabled={isSubmitting}
            />
          </div>
          <div className="flex gap-4 flex-col md:flex-row items-end">
            <Input
              label="Google Maps Link"
              name="googleMapsLink"
              value={formData.googleMapsLink}
              onChange={handleInputChange}
              disabled={isSubmitting}
              containerProps={{ className: 'min-w-0 flex-1' }}
            />
            {isLoadingCoordinates && <Spinner className="h-5 w-5" />}
            {googleMapsLinkStatus && (
              <span className={`text-xs whitespace-nowrap ${googleMapsLinkStatus.includes('Valid') ? 'text-green-600' : 'text-red-500'}`}>
                {googleMapsLinkStatus}
              </span>
            )}
          </div>
          <div className="flex gap-4 flex-col md:flex-row">
            <Input label="Longitude" name="lng" value={formData.lng} onChange={handleInputChange} disabled={isSubmitting} />
            <Input label="Latitude" name="lat" value={formData.lat} onChange={handleInputChange} disabled={isSubmitting} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="text" className="rounded-full" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" className="rounded-full" disabled={isSubmitting}>
              {isSubmitting ? <Spinner className="h-4 w-4" /> : (isUpdating ? 'Update' : 'Create')}
            </Button>
          </div>
        </div>
      </form>
    </ModalX>
  );
};

export default BranchCrudModal;
